/**
 * Design System - Visual Constants
 *
 * Centralized design tokens incorporating zone themes and platform colors
 * from the game for visual consistency between UI and gameplay.
 */

/**
 * Zone Theme Colors (from ZoneThemes.ts)
 * Each zone has a background color and accent for UI elements
 */
export const ZONE_THEMES = {
  zone1: {
    id: 1,
    name: 'Forest Floor',
    bg: 0x2d4a3e, // Dark forest green
    bgHex: '#2d4a3e',
    accent: 0x4caf50,
    accentHex: '#4CAF50',
    heightRange: [0, 2000],
    decorations: ['flower', 'frog', 'firefly', 'dewdrop', 'mushroom', 'butterfly'],
  },
  zone2: {
    id: 2,
    name: 'Tree Canopy',
    bg: 0x5a7a4a, // Olive green
    bgHex: '#5a7a4a',
    accent: 0x8bc34a,
    accentHex: '#8BC34A',
    heightRange: [2000, 4000],
    decorations: ['bird', 'nest', 'leaf'],
  },
  zone3: {
    id: 3,
    name: 'Sky Realm',
    bg: 0x5a7a9d, // Deep sky blue
    bgHex: '#5a7a9d',
    accent: 0x42a5f5,
    accentHex: '#42A5F5',
    heightRange: [4000, 6500],
    decorations: ['cloud', 'bird', 'stardust'],
  },
  zone4: {
    id: 4,
    name: 'Starlit Night',
    bg: 0x3d2e5f, // Deep purple/night sky
    bgHex: '#3d2e5f',
    accent: 0x9c27b0,
    accentHex: '#9C27B0',
    heightRange: [6500, 8500],
    decorations: ['stardust', 'aurora', 'shootingstar'],
  },
  zone5: {
    id: 5,
    name: 'Golden Summit',
    bg: 0x8d7330, // Dark gold/bronze
    bgHex: '#8d7330',
    accent: 0xffd700,
    accentHex: '#FFD700',
    heightRange: [8500, 10000],
    decorations: ['coin', 'gem', 'sparkle', 'trophy', 'crown'],
  },
  zone6: {
    id: 6,
    name: 'Ultimate Challenge',
    bg: 0xb87700, // Dark amber/orange
    bgHex: '#b87700',
    accent: 0xff6f00,
    accentHex: '#FF6F00',
    heightRange: [10000, 11500],
    decorations: ['lightning', 'particle'],
  },
} as const;

/**
 * Surface Type Colors (from ZoneThemes.ts)
 * Used for platform representations in UI
 */
export const SURFACE_COLORS = {
  NORMAL: {
    colors: [0x8b4513, 0xa0522d, 0xcd853f],
    hex: ['#8B4513', '#A0522D', '#CD853F'],
    name: 'Earth',
    icon: '🪨',
  },
  STICKY: {
    colors: [0x2e7d32, 0x388e3c, 0x4caf50],
    hex: ['#2E7D32', '#388E3C', '#4CAF50'],
    name: 'Moss',
    icon: '🌿',
  },
  SLIPPERY: {
    colors: [0x1976d2, 0x2196f3, 0x42a5f5],
    hex: ['#1976D2', '#2196F3', '#42A5F5'],
    name: 'Ice',
    icon: '❄️',
  },
  BOUNCY: {
    colors: [0xe65100, 0xff6f00, 0xff9800],
    hex: ['#E65100', '#FF6F00', '#FF9800'],
    name: 'Spring',
    icon: '🟠',
  },
  ANGLED: {
    colors: [0x616161, 0x757575, 0x9e9e9e],
    hex: ['#616161', '#757575', '#9E9E9E'],
    name: 'Stone',
    icon: '⬜',
  },
} as const;

/**
 * UI Color Palette
 * Extends zone colors with UI-specific colors
 */
export const UI_COLORS = {
  // Primary (Blobo theme - white/light)
  primary: '#FFFFFF',
  primaryDark: '#E0E0E0',
  primaryAlpha: 'rgba(255, 255, 255, 0.9)',

  // Accent (taken from Zone 5 - gold)
  accent: '#FFD700', // Gold
  accentDark: '#FFA000', // Dark gold
  accentLight: '#FFEB3B', // Light gold
  accentGlow: '#FFE873', // Glow effect

  // Background (deep space/night theme)
  bgDarkest: '#0A0E27', // Deep space blue
  bgDark: '#1A1F3A', // Night blue
  bgMid: '#2A2F4A', // Twilight blue
  bgLight: '#3A3F5A', // Light twilight

  // Status colors
  success: '#00FF88', // Bright green
  warning: '#FFAA00', // Orange
  error: '#FF4444', // Red
  disabled: '#666666', // Gray

  // UI surface colors (glassmorphism)
  glassLight: 'rgba(255, 255, 255, 0.1)',
  glassDark: 'rgba(0, 0, 0, 0.2)',
  glassBorder: 'rgba(255, 255, 255, 0.2)',

  // Shadows and overlays
  shadow: 'rgba(0, 0, 0, 0.5)',
  overlay: 'rgba(0, 0, 0, 0.7)',
} as const;

/**
 * Typography System
 */
export const TYPOGRAPHY = {
  // Font families
  fonts: {
    display: '"Fredoka One", "Baloo 2", "Nunito", Arial, sans-serif',
    body: 'Arial, "Helvetica Neue", sans-serif',
    mono: '"Space Mono", "Courier New", monospace',
    josefin: '"Josefin Sans", Arial, sans-serif',
  },

  // Font sizes (base sizes - will be scaled by devicePixelRatio)
  sizes: {
    hero: 64, // Main menu title
    title: 48, // Scene titles
    heading: 32, // Section headers
    subheading: 24, // Subsections
    body: 18, // Standard text
    caption: 14, // Descriptions, help text
    tiny: 12, // Fine print
  },

  // Font weights
  weights: {
    normal: 400,
    medium: 500,
    bold: 700,
    black: 900,
  },

  // Line heights
  lineHeights: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.8,
  },
} as const;

/**
 * Spacing System (8px grid)
 */
export const SPACING = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 64,
} as const;

/**
 * Border Radius
 */
export const RADIUS = {
  sm: 4,
  md: 8,
  lg: 16,
  xl: 24,
  round: 9999, // Fully rounded
} as const;

/**
 * Animation Durations (milliseconds)
 */
export const DURATION = {
  instant: 100,
  fast: 200,
  normal: 300,
  slow: 500,
  slower: 800,
  slowest: 1000,
} as const;

/**
 * Animation Easings
 */
export const EASING = {
  linear: 'Linear',
  easeIn: 'Quad.easeIn',
  easeOut: 'Quad.easeOut',
  easeInOut: 'Quad.easeInOut',
  bounceOut: 'Back.easeOut',
  bounceInOut: 'Back.easeInOut',
  elastic: 'Elastic.easeOut',
} as const;

/**
 * Shadows (CSS box-shadow format for DOM elements)
 */
export const SHADOWS = {
  sm: '0 2px 4px rgba(0, 0, 0, 0.1)',
  md: '0 4px 8px rgba(0, 0, 0, 0.15)',
  lg: '0 8px 16px rgba(0, 0, 0, 0.2)',
  xl: '0 16px 32px rgba(0, 0, 0, 0.25)',
  glow: '0 0 20px rgba(255, 215, 0, 0.5)', // Gold glow
} as const;

/**
 * Touch Targets
 */
export const TOUCH = {
  minSize: 44, // Minimum touch target size (44x44px iOS/Android guideline)
  recommended: 48, // Recommended size for better UX
} as const;

/**
 * Breakpoints (for responsive design)
 */
export const BREAKPOINTS = {
  small: 600, // Small phones
  medium: 900, // Large phones, small tablets
  large: 1200, // Tablets, desktop
} as const;

/**
 * Z-Index Layers
 */
export const Z_INDEX = {
  background: -10,
  platformDecorations: 0,
  platforms: 1,
  blobo: 10,
  particles: 15,
  uiBackground: 20,
  uiContent: 30,
  modal: 40,
  tooltip: 50,
  notification: 60,
} as const;

/**
 * Helper function to get zone theme by ID
 */
export function getZoneTheme(zoneId: number) {
  const key = `zone${zoneId}` as keyof typeof ZONE_THEMES;
  return ZONE_THEMES[key] || ZONE_THEMES.zone1;
}

/**
 * Helper function to create vertical gradient through all zones
 * Returns CSS gradient string for DOM elements
 */
export function createZoneGradientCSS(): string {
  return `linear-gradient(to top,
    ${ZONE_THEMES.zone1.bgHex} 0%,
    ${ZONE_THEMES.zone2.bgHex} 20%,
    ${ZONE_THEMES.zone3.bgHex} 40%,
    ${ZONE_THEMES.zone4.bgHex} 60%,
    ${ZONE_THEMES.zone5.bgHex} 80%,
    ${ZONE_THEMES.zone6.bgHex} 100%
  )`;
}

/**
 * Helper function to get color with alpha
 */
export function colorWithAlpha(hexColor: string, alpha: number): string {
  // Convert hex to rgba
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
